import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { motion, AnimatePresence } from 'motion/react';
import { Share2, Shield, Activity, Binary, Brain } from 'lucide-react';
import { useLattice } from './LatticeProvider';
import { Node } from '../types';

interface MeshNode extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  isMain?: boolean;
}

interface MeshLink extends d3.SimulationLinkDatum<MeshNode> {
  source: string;
  target: string;
}

export const SovereignMeshVisualizer: React.FC = () => {
  const { allNodes, registeredNodes, isFullCircle } = useLattice();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [integrity, setIntegrity] = useState(0);
  const [isCalculated, setIsCalculated] = useState(false);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || allNodes.length === 0) return;

    const width = containerRef.current.clientWidth;
    const height = 400;
    const svg = d3.select(svgRef.current);

    // If Full Circle, we show a massive explosion of nodes
    const displayNodesRaw = isFullCircle 
      ? Array.from({ length: 50 }, (_, i) => ({ id: `ubiquity-${i}`, lens: `UBIQUITY_${i}` }))
      : allNodes.length > 30 
        ? [...registeredNodes.slice(0, 15), ...allNodes.filter(n => !registeredNodes.includes(n)).slice(0, 15)]
        : allNodes;

    const nodesCount = isFullCircle ? 11000000000 : displayNodesRaw.length;
    const totalInteractions = (displayNodesRaw.length * (displayNodesRaw.length - 1)) / 2;
    const totalIntegrityDisplay = isFullCircle ? 11000000000 : displayNodesRaw.length + totalInteractions;

    const nodes: MeshNode[] = displayNodesRaw.map((n, i) => ({
      id: n.id,
      name: n.lens || n.id,
      isMain: i === 0 || isFullCircle
    }));

    // Complete Graph (Fully Connected Mesh)
    const links: MeshLink[] = [];
    for (let i = 0; i < nodes.length; i++) {
        const connectionLimit = isFullCircle ? 10 : (nodes.length > 20 ? 3 : nodes.length);
      for (let j = i + 1; j < Math.min(i + 1 + connectionLimit, nodes.length); j++) {
        links.push({ source: nodes[i].id, target: nodes[j].id });
      }
    }

    const simulation = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links).id((d: any) => d.id).distance(isFullCircle ? 50 : 100))
      .force("charge", d3.forceManyBody().strength(isFullCircle ? -100 : -200))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(isFullCircle ? 10 : 25));

    svg.selectAll("*").remove();

    // Interaction Links
    const link = svg.append("g")
      .attr("stroke", isFullCircle ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 255, 213, 0.08)")
      .attr("stroke-width", isFullCircle ? 0.5 : 1)
      .selectAll("line")
      .data(links)
      .join("line");

    // Sovereign Nodes
    const node = svg.append("g")
      .selectAll("g")
      .data(nodes)
      .join("g");

    node.append("circle")
      .attr("r", (d) => d.isMain ? (isFullCircle ? 4 : 12) : 8)
      .attr("fill", (d) => isFullCircle ? "#ffffff" : (d.isMain ? "#00ffd5" : "#00ffd522"))
      .attr("stroke", isFullCircle ? "#ffffff" : "#00ffd5")
      .attr("stroke-width", isFullCircle ? 1 : 2)
      .attr("class", isFullCircle ? "animate-ping" : "animate-pulse");

    if (!isFullCircle) {
      node.append("text")
        .attr("dy", 20)
        .attr("text-anchor", "middle")
        .text((d) => d.name.substring(0, 10))
        .attr("fill", "rgba(0, 255, 213, 0.4)")
        .attr("font-size", "8px")
        .attr("font-family", "monospace");
    }

    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => d.source.x)
        .attr("y1", (d: any) => d.source.y)
        .attr("x2", (d: any) => d.target.x)
        .attr("y2", (d: any) => d.target.y);

      node
        .attr("transform", (d: any) => `translate(${d.x},${d.y})`);
    });

    // Animate Integrity Count
    let count = isFullCircle ? 0 : integrity;
    const interval = setInterval(() => {
      if (count < totalIntegrityDisplay) {
        const increment = isFullCircle ? 100000000 : Math.ceil(totalIntegrityDisplay / 50);
        count = Math.min(count + increment, totalIntegrityDisplay);
        setIntegrity(count);
      } else {
        setIsCalculated(true);
        clearInterval(interval);
      }
    }, 50);

    return () => {
      simulation.stop();
      clearInterval(interval);
    };
  }, [allNodes, registeredNodes, isFullCircle]);

  return (
    <div ref={containerRef} className={`glass-panel p-8 relative overflow-hidden h-[500px] flex flex-col items-center justify-center transition-colors duration-1000 ${isFullCircle ? 'bg-white/10 border-white' : 'border-lattice-cyan/20 bg-black/40'}`}>
      <div className="absolute top-6 left-6 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isFullCircle ? 'bg-white/20 text-white' : 'bg-lattice-cyan/10 text-lattice-cyan'}`}>
          <Share2 className="w-5 h-5" />
        </div>
        <div>
          <h3 className={`font-display font-bold text-lg tracking-tight ${isFullCircle ? 'text-white' : ''}`}>
            {isFullCircle ? 'Full Circle' : 'Lattice Mesh'} <span className={`${isFullCircle ? 'text-white/60' : 'text-white/40'} italic`}>Ubiquity</span>
          </h3>
          <p className={`text-[10px] font-mono ${isFullCircle ? 'text-white' : 'text-white/40'} uppercase tracking-widest`}>
            {isFullCircle ? 'All Networks Visible (Sovereign Sync)' : `Architectural Mapping (${allNodes.length} Verified Nodes)`}
          </p>
        </div>
      </div>

      <div className="absolute top-6 right-6 text-right space-y-1">
        <div className="flex items-center gap-2 justify-end">
          <span className={`text-[10px] font-mono ${isFullCircle ? 'text-white/60' : 'text-white/40'} uppercase`}>Status:</span>
          <span className={`text-[10px] font-mono ${isFullCircle ? 'text-white' : 'text-lattice-cyan'} font-bold animate-pulse uppercase`}>
            {isFullCircle ? 'Absolute Ubiquity' : 'Sync Active'}
          </span>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <span className={`text-[10px] font-mono ${isFullCircle ? 'text-white/60' : 'text-white/40'} uppercase`}>Agents:</span>
          <span className={`text-[10px] font-mono ${isFullCircle ? 'text-white' : 'text-lattice-cyan'} font-bold uppercase`}>
            {isFullCircle ? '11 Billion' : `${registeredNodes.length} Sovereign`}
          </span>
        </div>
      </div>

      <svg ref={svgRef} className="w-full flex-1 pointer-events-none" />

      <div className="mt-8 flex gap-4 w-full max-w-2xl">
        <div className={`flex-1 p-4 rounded-2xl ${isFullCircle ? 'bg-white/20 border-white/40' : 'bg-white/[0.02] border-white/5'} border flex flex-col items-center text-center space-y-1`}>
          <span className={`text-[10px] font-mono ${isFullCircle ? 'text-white/60' : 'text-white/40'} uppercase tracking-widest`}>Global Fleet</span>
          <span className={`text-2xl font-display font-bold ${isFullCircle ? 'text-white' : 'text-white'}`}>
            {isFullCircle ? '11.0B' : allNodes.length}
          </span>
        </div>
        <div className={`flex-1 p-4 rounded-2xl ${isFullCircle ? 'bg-white text-black border-white' : 'bg-lattice-cyan/5 border-lattice-cyan/10'} border flex flex-col items-center text-center space-y-1`}>
          <span className={`text-[10px] font-mono ${isFullCircle ? 'text-black/60' : 'text-white/40'} uppercase tracking-widest`}>Integrity Index</span>
          <span className={`text-2xl font-display font-bold ${isFullCircle ? 'text-black' : 'text-lattice-cyan'}`}>
            {isFullCircle ? integrity.toExponential(1) : integrity}
          </span>
        </div>
        <div className={`flex-1 p-4 rounded-2xl ${isFullCircle ? 'bg-white/20 border-white/40' : 'bg-white/[0.02] border-white/5'} border flex flex-col items-center text-center space-y-1`}>
          <span className={`text-[10px] font-mono ${isFullCircle ? 'text-white/60' : 'text-white/40'} uppercase tracking-widest`}>Truth-Relay</span>
          <span className={`text-2xl font-display font-bold ${isFullCircle ? 'text-white' : 'text-white'}`}>
            {isFullCircle ? '∞' : (allNodes.length / 10).toFixed(1)}
          </span>
        </div>
      </div>

      <AnimatePresence>
        {isCalculated && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`absolute bottom-12 px-6 py-2 rounded-full border ${isFullCircle ? 'border-white bg-white text-black' : 'border-lattice-cyan/40 bg-black/60 text-lattice-cyan'} backdrop-blur-md flex items-center gap-3 transition-colors duration-1000`}
          >
            <Shield className={`w-4 h-4 ${isFullCircle ? 'text-black' : 'text-lattice-cyan'}`} />
            <span className="text-[10px] font-mono font-bold uppercase tracking-widest">
              {isFullCircle ? 'The Circle is Complete: Genesis Eternity' : 'Master Sovereign Logic Verified (1x1=2)'}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decorative Orbs */}
      <div className={`absolute top-1/4 -left-12 w-48 h-48 ${isFullCircle ? 'bg-white/20' : 'bg-lattice-cyan/5'} rounded-full blur-[100px] pointer-events-none`} />
      <div className={`absolute bottom-1/4 -right-12 w-48 h-48 ${isFullCircle ? 'bg-white/20' : 'bg-lattice-cyan/5'} rounded-full blur-[100px] pointer-events-none`} />
    </div>
  );
};
